import { apiClient } from './authApi';

export interface Notification {
  id: string;
  userId: number;
  type: string;
  message: string;
  isRead: boolean;
  metadata?: any;
  createdAt: Date;
  updatedAt: Date;
}

export interface PaginatedNotifications {
  items: Notification[];
  total: number;
}

export interface NotificationResponse {
  success: boolean;
  data: Notification | Notification[] | { count: number } | PaginatedNotifications;
  message?: string;
}

class NotificationApi {
  /**
   * Get notifications for the current user (paginated, latest first)
   */
  async getNotifications(limit: number = 50, offset: number = 0): Promise<PaginatedNotifications> {
    const response = await apiClient.get<NotificationResponse>(
      `/notifications?limit=${limit}&offset=${offset}`
    );
    return response.data.data as PaginatedNotifications;
  }

  /**
   * Get unread notifications for the current user
   */
  async getUnreadNotifications(): Promise<Notification[]> {
    const response = await apiClient.get<NotificationResponse>('/notifications/unread');
    return response.data.data as Notification[];
  }

  /**
   * Get unread notification count
   */
  async getUnreadCount(): Promise<number> {
    const response = await apiClient.get<NotificationResponse>('/notifications/unread-count');
    return (response.data.data as { count: number }).count;
  }

  /**
   * Mark a notification as read
   */
  async markAsRead(id: string): Promise<Notification> {
    const response = await apiClient.put<NotificationResponse>(`/notifications/${id}/read`);
    return response.data.data as Notification;
  }

  /**
   * Mark all notifications as read
   */
  async markAllAsRead(): Promise<number> {
    const response = await apiClient.put<NotificationResponse>('/notifications/read-all');
    return (response.data.data as { count: number }).count;
  }

  /**
   * Delete a notification
   */
  async deleteNotification(id: string): Promise<void> {
    await apiClient.delete(`/notifications/${id}`);
  }
}

export const notificationApi = new NotificationApi();
